import { Component } from "../../Component.js";
import { getTypeByName, Kinds, Types } from "../../../data/model/data-types.js";
import { default as numeral } from "numeral";

function resolveValue(value, context = null) {

    return typeof value === 'function' ? value.call(context) : value;

}

function normalizeFieldName(name = '') {

    return String(name || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_\-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (value) => value.toUpperCase());

}

function getFieldType(field) {

    return field?.datatype || getTypeByName(field?.type) || null;

}

function getFieldValue(field, data) {

    if (!field)
        return undefined;
    if (typeof field.getValue == 'function')
        return field.getValue(data);
    return data ? data[field.name] : undefined;

}

function formatDisplayValue(field, value) {

    if (value === null || value === undefined)
        return '';
    if (typeof field?.formatValue == 'function')
        return field.formatValue(value);
    return String(value);

}

function parseNumber(text, integer = false) {

    if (text === null || text === undefined || text === '')
        return null;

    const parsedValue = numeral(text).value();

    if (parsedValue === null || Number.isNaN(parsedValue))
        return NaN;

    return integer ? Math.trunc(parsedValue) : parsedValue;

}

export class ObjectInspector extends Component {

    constructor(parent, options = {}) {

        options = Object.assign({
            model: null,
            value: null,
            readOnly: false,
            fields: null,
            include: null,
            pinnedFieldNames: null,
            expandedFieldNames: null,
            fieldErrors: null,
            onFieldValueChange: null,
            onChange: null,
            emptyText: 'No inspectable fields available.',
        }, options || {});

        super(parent, options);

        this.model = options.model;
        this.value = options.value;
        this.readOnly = options.readOnly;
        this.fields = options.fields;
        this.include = options.include;
        this.pinnedFieldNames = options.pinnedFieldNames;
        this.expandedFieldNames = options.expandedFieldNames;
        this.fieldErrors = options.fieldErrors;
        this.onFieldValueChange = options.onFieldValueChange;
        this.onChange = options.onChange;
        this.emptyText = options.emptyText;

        this.expanded = new Set(options.expandedFieldNames || []);
        this.localFieldErrors = new Map();

    };

    getModel() { return resolveValue(this.model, this); };

    getValue() { return resolveValue(this.value, this); };

    isReadOnly() { return resolveValue(this.readOnly, this) || false; };

    getPinnedFieldNames() { return resolveValue(this.pinnedFieldNames, this) || []; };

    getModelLabel() {

        const model = this.getModel();
        return model?.getLabel?.() || model?.label || model?.getEntityName?.() || model?.entityName || 'Data Model';

    };

    getFields() {

        const fields = resolveValue(this.fields, this);
        const model = this.getModel();
        const sourceFields = fields || model?.getFields?.() || model?.getFieldDefs?.() || [];
        const include = this.include || ((field) => field && field.name);
        const pinnedFieldNames = this.getPinnedFieldNames();
        const pinnedIndex = (field) => pinnedFieldNames.indexOf(field.name);

        return (sourceFields || [])
            .filter((field) => include(field))
            .slice()
            .sort((a, b) => {
                const aPinnedIndex = pinnedIndex(a);
                const bPinnedIndex = pinnedIndex(b);

                if (aPinnedIndex >= 0 || bPinnedIndex >= 0) {
                    if (aPinnedIndex < 0)
                        return 1;
                    if (bPinnedIndex < 0)
                        return -1;
                    return aPinnedIndex - bPinnedIndex;
                }

                return this.getFieldLabel(a).localeCompare(this.getFieldLabel(b));
            });

    };

    getFieldLabel(field) {

        return field?.label || normalizeFieldName(field?.name || 'Field');

    };

    getFieldDescription(field) {

        return field?.description || null;

    };

    getFieldUnit(field) {

        return field?.unit || null;

    };

    isFieldExpanded(field) {

        return this.expanded.has(field?.name);

    };

    toggleField(field, vm) {

        if (!field?.name)
            return;

        if (this.expanded.has(field.name))
            this.expanded.delete(field.name);
        else
            this.expanded.add(field.name);

        if (vm && typeof vm.invalidate == 'function')
            vm.invalidate();
        else
            this.invalidate();

    };

    getFieldErrors(field) {

        const data = this.getValue();
        const errors = [];

        if (field?.required && (getFieldValue(field, data) === null || getFieldValue(field, data) === undefined || getFieldValue(field, data) === ''))
            errors.push('Required value is missing.');

        if (typeof field?.getValueError == 'function')
            errors.push(...(field.getValueError(data) || []));

        const localError = this.localFieldErrors.get(field?.name);
        if (localError)
            errors.push(localError);

        const fieldErrors = resolveValue(this.fieldErrors, this);
        const externalErrors = typeof fieldErrors == 'function'
            ? fieldErrors.call(this, field.name, field, data)
            : fieldErrors instanceof Map
                ? fieldErrors.get(field.name)
                : fieldErrors?.[field.name];

        if (Array.isArray(externalErrors))
            errors.push(...externalErrors);
        else if (externalErrors)
            errors.push(externalErrors);

        return errors;

    };

    canEditField(field) {

        return !this.isReadOnly() && !field?.readOnly && (typeof this.onFieldValueChange == 'function' || typeof this.onChange == 'function');

    };

    commitFieldValue(field, value) {

        const data = this.getValue();

        if (!field || !data || !this.canEditField(field))
            return false;

        this.localFieldErrors.delete(field.name);

        if (typeof this.onFieldValueChange == 'function') {
            const result = this.onFieldValueChange.call(this, value, field, data);
            if (result === false)
                return false;
        }
        else if (typeof this.onChange == 'function') {
            const nextValue = Object.assign({}, data, { [field.name]: value });
            const result = this.onChange.call(this, nextValue, { field, fieldName: field.name, value, previousValue: getFieldValue(field, data) });
            if (result === false)
                return false;
        }

        this.invalidate();
        return true;

    };

    async _prepare(options) {

        await super._prepare(options);

        const model = this.getModel();
        if (model && typeof model.prepare == 'function')
            await model.prepare();

    };

    _renderHeader(r) {

        return r.row({ css: 'min' }, [
            r.strong('object-inspector__title max', ['Object Inspector']),
            r.span('object-inspector__model', [this.getModelLabel()]),
        ]);

    };

    _renderFieldRows(r, vm, field) {

        const data = this.getValue();
        const value = getFieldValue(field, data);
        const errors = this.getFieldErrors(field);
        const hasErrors = errors && errors.length > 0;
        const description = this.getFieldDescription(field);
        const expanded = this.isFieldExpanded(field);
        const canExpand = !!description || hasErrors;
        const isPinned = this.getPinnedFieldNames().includes(field.name);

        const fieldLabel = this.getFieldLabel(field);
        const unit = this.getFieldUnit(field) || null;
        const hasUnit = unit !== null && unit !== undefined && unit !== '';

        const row = r.row(
            {
                css: [
                    'object-inspector__row',
                    hasErrors ? 'object-inspector__row--invalid' : null,
                    isPinned ? 'object-inspector__row--pinned' : null,
                ].filter(Boolean).join(' ')
            },
            [
                (hasErrors ? r.icon('#error', { css: 'object-inspector__icon object-inspector__icon--error' }) : r.div({ css: 'object-inspector__icon' })),
                (isPinned ? r.icon('#pin', { css: 'object-inspector__icon object-inspector__icon--pinned' }) : r.div({ css: 'object-inspector__icon' })),
                r.div({ css: 'object-inspector__label max', props: { title: fieldLabel } }, [fieldLabel]),
                (hasUnit ? r.div({ css: 'object-inspector__unit' }, [unit]) : null),
                r.div({ css: 'object-inspector__value' }, [this._renderFieldValue(r, vm, field, value, errors)]),
                (canExpand ? this._renderToggleButton(r, vm, field, expanded) : r.div({ css: 'object-inspector__icon' })),
            ].filter(Boolean)
        );

        if (!expanded)
            return [row];

        return [
            row,
            r.row(
                { css: 'object-inspector__details-row' },
                [
                    hasErrors ? r.div({ css: 'object-inspector__errors' }, errors.map((error) => r.div({ css: 'object-inspector__error' }, [error]))) : null,
                    description ? r.div({ css: 'object-inspector__description' }, [description]) : null,
                ].filter((item) => item !== null && item !== undefined && item !== '')
            ),
        ];

    };

    _renderToggleButton(r, vm, field, expanded) {

        return r.icon('#chevron-right', {
            css: `object-inspector__icon object-inspector__toggle ${expanded ? 'fa-rotate-90' : ''}`,
            props: {
                title: expanded ? 'Collapse field details' : 'Expand field details',
                onclick: () => this.toggleField(field, vm),
            },
        });

    };

    _renderFieldValue(r, vm, field, value, errors = []) {

        const type = getFieldType(field);
        const readOnly = !this.canEditField(field);
        const props = {
            readonly: readOnly ? 'readonly' : null,
            disabled: readOnly && type?.kind == Kinds.Bool ? 'disabled' : null,
            'aria-invalid': errors.length ? 'true' : null,
            title: [field?.type ? `Type: ${field.type}` : null, field?.required ? 'Required' : null, field?.readOnly ? 'Read-only' : null]
                .filter(Boolean)
                .join(', ') || null,
        };

        if (type?.kind == Kinds.Bool) {
            return r.checkBox(Boolean(value), null, {
                css: 'object-inspector__checkbox',
                props,
                onValueSet: (nextValue) => this.commitFieldValue(field, nextValue),
            });
        }

        if (type?.kind == Kinds.Number) {
            const integer = field.type == Types.Int || field.datatypeId == Types.Int;
            return r.textBox(value, {
                css: 'object-inspector__editor object-inspector__editor--number',
                props,
                onFormatValue: (currentValue) => formatDisplayValue(field, currentValue),
                onParseText: (text) => parseNumber(text, integer),
                onValueSet: (nextValue) => {
                    if (Number.isNaN(nextValue)) {
                        this.localFieldErrors.set(field.name, 'Invalid number format.');
                        this.expanded.add(field.name);
                        this.invalidate();
                        return false;
                    }
                    return this.commitFieldValue(field, nextValue);
                },
            });
        }

        if (type?.kind == Kinds.String) {
            return r.textBox(value, {
                css: 'object-inspector__editor',
                props,
                multiline: !!field.multiline,
                onValueSet: (nextValue) => this.commitFieldValue(field, nextValue),
            });
        }

        return r.el('span', 'object-inspector__readonly-value', null, formatDisplayValue(field, value));

    };

    _renderContent(r, vm) {

        const fields = this.getFields();

        if (!fields.length) {
            return r.empty({
                title: this.emptyText,
                icon: '#info',
            });
        }

        return r.div('scroll-y', [
            r.stack({ css: 'vertical' }, fields.flatMap((field) => this._renderFieldRows(r, vm, field)))
        ]);

    };

    _render(r, vm) {

        const css = this.getCss() ?? '';

        return r.stack({
            css: `vertical object-inspector ${css}`
        }, [
            this._renderHeader(r),
            this._renderContent(r, vm),
        ]);

    };

}
